import { useEffect, useState } from "react";

import { PersistedTableState, TablePersistenceConfig } from "./types";

/**
 * Hook to manage persisted table state (filters, page, search) in sessionStorage.
 *
 * @param config Configuration for table persistence including storage key and default filters.
 * @param options Optional initial values for page, size, query, and override filters.
 * @returns An object containing current filters, page, search and their respective setters.
 *
 * Usage:
 * const { filters, setFilters, page, setPage, search, setSearch } = usePersistedTableState<FilterType>(
 *   {
 *     storageKey: "my_table",
 *     createDefaultFilters: () => ({ status: null, tags: [] }),
 *   },
 *   {
 *     initialPage: 0,
 *     initialSize: 20,
 *     initialQuery: "",
 *   }
 * );
 *
 * <DataTable
 *   onPageChange={(newPage) => setPage((prev) => ({ ...prev, index: newPage }))}
 *   onPageSizeChange={(newSize) => setPage((prev) => ({ ...prev, size: newSize }))}
 *   onSearchChange={setSearch}
 *   page={page.index}
 *   pageSize={page.size}
 *   search={search}
 * />
 */

const PREFIX_KEY = "cortex_table";

export function usePersistedTableState<F>(
  config: TablePersistenceConfig<F>,
  options?: {
    initialPage?: number;
    initialSize?: number;
    initialQuery?: string;
    overrideFilters?: F | null;
  },
) {
  const { storageKey, createDefaultFilters } = config;
  const initialQuery = options?.initialQuery ?? "";
  const overrideFilters = options?.overrideFilters ?? null;

  // Filters
  const [filters, setFilters] = useState<F>(() => {
    if (overrideFilters) {
      return overrideFilters;
    }

    const saved = sessionStorage.getItem(`${PREFIX_KEY}_${storageKey}`);
    if (!saved) return createDefaultFilters();

    try {
      const parsed = JSON.parse(saved) as unknown;

      // New format: { filters, page, search }
      if (
        parsed &&
        typeof parsed === "object" &&
        "filters" in parsed &&
        parsed.filters != null
      ) {
        return (parsed as PersistedTableState<F>).filters;
      }

      // Last resort: default
      return createDefaultFilters();
    } catch {
      return createDefaultFilters();
    }
  });

  // Search
  const [search, setSearch] = useState<string>(() => {
    // If caller provides an initialQuery (NavState), let that win
    if (initialQuery) return initialQuery;

    const saved = sessionStorage.getItem(`${PREFIX_KEY}_${storageKey}`);
    if (!saved) return "";

    try {
      const parsed = JSON.parse(saved) as unknown;
      if (
        parsed &&
        typeof parsed === "object" &&
        "search" in parsed &&
        (parsed as any).search != null
      ) {
        return String((parsed as any).search);
      }
    } catch {
      // ignore
    }

    return "";
  });

  // Page { index, size }
  const [page, setPage] = useState<{ index: number; size: number }>(() => {
    const defaultIndex = options?.initialPage ?? 0;
    const defaultSize = options?.initialSize ?? 10;

    // If caller explicitly provided page/size, prefer that
    if (
      options?.initialPage !== undefined ||
      options?.initialSize !== undefined
    ) {
      return {
        index: options?.initialPage ?? 0,
        size: options?.initialSize ?? 10,
      };
    }

    const saved = sessionStorage.getItem(`${PREFIX_KEY}_${storageKey}`);
    if (!saved) return { index: defaultIndex, size: defaultSize };

    try {
      const parsed = JSON.parse(saved) as unknown;

      if (
        parsed &&
        typeof parsed === "object" &&
        "page" in parsed &&
        (parsed as any).page != null
      ) {
        const storedPage = (parsed as any).page;

        // New format: { page: { index, size } }
        if (
          typeof storedPage === "object" &&
          storedPage !== null &&
          typeof storedPage.index === "number"
        ) {
          return {
            index: storedPage.index,
            size:
              typeof storedPage.size === "number"
                ? storedPage.size
                : defaultSize,
          };
        }

        // Legacy format: { page: number }
        if (typeof storedPage === "number") {
          return { index: storedPage, size: defaultSize };
        }
      }
    } catch {
      // ignore
    }

    return { index: defaultIndex, size: defaultSize };
  });

  // Persist both on change
  useEffect(() => {
    const toSave: PersistedTableState<F> = {
      filters,
      page,
      search,
    };

    sessionStorage.setItem(
      `${PREFIX_KEY}_${storageKey}`,
      JSON.stringify(toSave),
    );
  }, [filters, page, search, storageKey]);

  return {
    filters,
    page,
    search,
    setFilters,
    setPage,
    setSearch,
  };
}
